import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { useAuth } from '../context/AuthContext';
import { usePolling } from '../hooks/usePolling';
import { useThemeColors } from '../hooks/useThemeColors';
import ControlPanel from '../components/control/ControlPanel';
import BoxGrid from '../components/game/BoxGrid';
import PrizeBoard from '../components/game/PrizeBoard';
import GameHeader from '../components/game/GameHeader';
import OfferDisplay from '../components/game/OfferDisplay';
import Navbar from '../components/layout/Navbar';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { Plus, Share2, Check, CreditCard } from 'lucide-react';

const DEFAULT_PRIZES = [
  0.01, 1, 5, 10, 25, 50, 75, 100, 200, 500,
  1000, 5000, 10000, 15000, 20000, 30000, 50000, 75000, 100000, 200000,
];

const mobileCSS = `
@media (max-width: 900px) {
  .cr-content { flex-direction: column !important; }
  .cr-right { width: 100% !important; }
  .cr-grid-board { flex-direction: column !important; }
  .cr-board-side { width: 100% !important; }
}
`;

const pageStyles = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-bg-primary)',
  },
  content: {
    display: 'flex',
    gap: 'var(--space-md)',
    padding: 'var(--space-md)',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  left: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-md)',
    minWidth: 0,
  },
  right: {
    width: '340px',
    flexShrink: 0,
  },
  noGame: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-lg)',
    padding: 'var(--space-xl)',
    textAlign: 'center',
    minHeight: '50vh',
  },
  noGameText: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.2rem',
    color: 'var(--color-text-muted)',
    margin: 0,
  },
  gridAndBoard: {
    display: 'flex',
    gap: 'var(--space-md)',
    alignItems: 'flex-start',
  },
  boardSide: {
    width: '240px',
    flexShrink: 0,
  },
  gridSide: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-md)',
    minWidth: 0,
  },
};

export default function ControlRoom() {
  const { canCreateGame } = useAuth();
  const {
    currentGame,
    loading,
    error,
    clearError,
    fetchGames,
    createGame,
    fetchGameControl,
    selectBox,
    openBox,
    makeOffer,
    respondOffer,
    completeGame,
    setCurrentGame,
  } = useGame();

  const [showNewGame, setShowNewGame] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [playerRegion, setPlayerRegion] = useState('');
  const [copied, setCopied] = useState(false);

  useThemeColors(currentGame?.theme);

  const liveUrl = currentGame?.game_code
    ? `${window.location.origin}/live/${currentGame.game_code}`
    : null;

  const handleCopyLink = async () => {
    if (!liveUrl) return;
    try {
      await navigator.clipboard.writeText(liveUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback
      const input = document.createElement('input');
      input.value = liveUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const pollGameControl = useCallback(async () => {
    if (!currentGame?.id) return;
    try {
      await fetchGameControl(currentGame.id, { skipIfUnchanged: true });
    } catch {
      // silently ignore
    }
  }, [currentGame?.id, fetchGameControl]);

  useEffect(() => {
    const loadActiveGame = async () => {
      try {
        const data = await fetchGames({ status: 'playing' });
        const games = data.games || data;
        if (Array.isArray(games) && games.length > 0) {
          await fetchGameControl(games[0].id);
        }
      } catch {
        // no active game
      }
    };
    loadActiveGame();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  usePolling(pollGameControl, 3000, !!currentGame?.id && !isCompleted);

  const handleCreateGame = async (e) => {
    e.preventDefault();
    try {
      const result = await createGame({
        player_name: playerName,
        player_region: playerRegion,
      });
      const game = result.game || result;
      if (game.id) {
        await fetchGameControl(game.id);
      }
      setShowNewGame(false);
      setPlayerName('');
      setPlayerRegion('');
    } catch {
      // error handled by context
    }
  };

  const handleSelectBox = async (boxNumber) => {
    if (currentGame?.id) {
      await selectBox(currentGame.id, boxNumber);
      await fetchGameControl(currentGame.id);
    }
  };

  const handleOpenBox = async (boxNumber) => {
    if (currentGame?.id) {
      await openBox(currentGame.id, boxNumber);
    }
  };

  const handleMakeOffer = async (offerData) => {
    if (currentGame?.id) {
      await makeOffer(currentGame.id, offerData);
    }
  };

  const handleRespondOffer = async (accepted) => {
    if (currentGame?.id) {
      await respondOffer(currentGame.id, accepted);
    }
  };

  const handleCompleteGame = async () => {
    if (currentGame?.id) {
      await completeGame(currentGame.id);
    }
  };

  const isSetup = currentGame?.status === 'setup';
  const isCompleted = currentGame?.status === 'completed' || currentGame?.status === 'cancelled';
  const needsBoxSelection = isSetup && !currentGame?.player_box_number;
  const isPlaying = currentGame?.status === 'playing';
  const isOfferPending = currentGame?.status === 'offer_pending';
  const canOpenBoxes = isPlaying && !loading;

  const handleNewGame = () => {
    setCurrentGame(null);
    setShowNewGame(true);
  };

  return (
    <div style={pageStyles.container}>
      <style>{mobileCSS}</style>
      <Navbar />

      {currentGame ? (
        <>
          <GameHeader game={currentGame} />
          <div className="cr-content" style={pageStyles.content}>
            <div style={pageStyles.left}>
              <div className="cr-grid-board" style={pageStyles.gridAndBoard}>
                <div className="cr-board-side" style={pageStyles.boardSide}>
                  <PrizeBoard
                    boxes={currentGame.boxes || []}
                    allPrizes={currentGame.all_prizes && currentGame.all_prizes.length > 0 ? currentGame.all_prizes : DEFAULT_PRIZES}
                  />
                </div>
                <div style={pageStyles.gridSide}>
                  {isOfferPending && currentGame.last_offer && (
                    <OfferDisplay lastOffer={currentGame.last_offer} />
                  )}

                  <BoxGrid
                    boxes={currentGame.boxes || []}
                    playerBoxNumber={currentGame.player_box_number}
                    onBoxClick={needsBoxSelection ? handleSelectBox : handleOpenBox}
                    disabled={!canOpenBoxes && !needsBoxSelection}
                    selectionMode={needsBoxSelection}
                    showAllPrizes
                  />
                </div>
              </div>
            </div>

            <div className="cr-right" style={pageStyles.right}>
              {liveUrl && (
                <div style={{
                  marginBottom: 'var(--space-lg)',
                  padding: 'var(--space-md)',
                  backgroundColor: 'var(--color-bg-card)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(212, 175, 55, 0.1)',
                }}>
                  <div style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--color-text-muted)',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    marginBottom: 'var(--space-sm)',
                  }}>
                    Link schermo live
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: 'var(--space-sm)',
                    alignItems: 'center',
                  }}>
                    <div style={{
                      flex: 1,
                      padding: '6px 10px',
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid rgba(212, 175, 55, 0.08)',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.75rem',
                      color: 'var(--color-text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {liveUrl}
                    </div>
                    <Button
                      variant={copied ? 'primary' : 'secondary'}
                      size="sm"
                      onClick={handleCopyLink}
                      style={{ flexShrink: 0 }}
                    >
                      {copied ? <Check size={14} /> : <Share2 size={14} />}
                      {copied ? 'Copiato!' : 'Copia'}
                    </Button>
                  </div>
                </div>
              )}
              <ControlPanel
                game={currentGame}
                onMakeOffer={handleMakeOffer}
                onRespondOffer={handleRespondOffer}
                onCompleteGame={handleCompleteGame}
                loading={loading}
              />
              {isCompleted && (
                <div style={{ marginTop: 'var(--space-md)' }}>
                  <Button variant="primary" size="lg" onClick={handleNewGame} style={{ width: '100%' }}>
                    <Plus size={18} />
                    Nuova Partita
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div style={pageStyles.noGame}>
          {loading ? (
            <LoadingSpinner size="lg" />
          ) : (
            <>
              <p style={pageStyles.noGameText}>
                Nessuna partita in corso
              </p>
              {canCreateGame ? (
                <Button variant="primary" size="lg" onClick={() => setShowNewGame(true)}>
                  <Plus size={20} />
                  Nuova Partita
                </Button>
              ) : (
                <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-md)' }}>
                  <p style={{ ...pageStyles.noGameText, fontSize: '0.95rem' }}>
                    Abbonati per creare nuove partite
                  </p>
                  <Link to="/admin/subscription" style={{ textDecoration: 'none' }}>
                    <Button variant="primary" size="lg">
                      <CreditCard size={20} />
                      Vedi piani — 9,99 / mese
                    </Button>
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <div
          onClick={clearError}
          style={{
            position: 'fixed',
            bottom: 'var(--space-lg)',
            right: 'var(--space-lg)',
            backgroundColor: 'rgba(231, 76, 60, 0.9)',
            color: '#fff',
            padding: 'var(--space-md) var(--space-lg)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: '400px',
            fontSize: '0.9rem',
            zIndex: 1000,
            cursor: 'pointer',
          }}
        >
          {error}
        </div>
      )}

      <Modal
        isOpen={showNewGame}
        onClose={() => setShowNewGame(false)}
        title="Nuova Partita"
      >
        <form onSubmit={handleCreateGame} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <Input
            label="Nome del giocatore"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="Mario Rossi"
            required
          />
          <Input
            label="Provenienza"
            value={playerRegion}
            onChange={(e) => setPlayerRegion(e.target.value)}
            placeholder="Lombardia"
          />
          <Button variant="primary" type="submit" loading={loading} style={{ width: '100%' }}>
            <Plus size={16} />
            Inizia partita
          </Button>
        </form>
      </Modal>
    </div>
  );
}
